<template>
  <div class="task" :data-cy="id">
    <Grid
      v-ripple
      :gtc="isCount0 ? '1fr' : '1fr 45px'"
      class="border-radius-sm overflow-hidden cursor-pointer relative-position full-height"
      :class="isCount0 ? 'bg-white-3' : 'shadow2'"
      @click.native="openTaskDialog"
    >
      <div class="text-body-1 text-ellipsis q-pa-sm" :class="isCount0 ? 'text-grey3' : 'text-secondary-2'">
        {{ name }}
      </div>

      <Grid v-if="!isCount0" content-items="center" class="count text-body-2 bg-primary-1 text-white-2">
        <template v-if="count">{{ count }}</template>
        <q-spinner v-else size="1.2rem"></q-spinner>
      </Grid>
    </Grid>

    <q-dialog
      content-class="workflow-task-modal"
      :value="isDialogOpen"
      full-width
      full-height
      @input="!dialogLoader && closeDialog()"
    >
      <Grid gtr="auto 1fr" class="task-modal bg-white-1">
        <Grid gtc="1fr auto" class="header shadow3">
          <div class="text-heading-1 text-secondary-2 q-pl-lg q-pt-lg q-pb-md">
            {{ dialogName }} <HelpPageBtn :page="page" />
            <div v-if="subtitle" class="text-overline-1 text-grey3">{{ subtitle }}</div>
          </div>
          <CloseIcon class="q-pt-md" style="align-items: start" @click="closeDialog()" />
        </Grid>

        <component
          :is="dialogComponent"
          ref="dialogContent"
          :period-id="periodId"
          @make-count-0="closeDialog(0)"
          @set-loader="dialogLoader = $event"
          @set-subtitle="subtitle = $event"
        />

        <QInnerLoading :showing="dialogLoader" style="z-index: 2">
          <QSpinnerHourglass size="3.5rem" color="primary-1" />
        </QInnerLoading>
      </Grid>
    </q-dialog>
  </div>
</template>

<script>
// Libraries
import { startCase } from 'lodash'
// Components
import HelpPageBtn from '@/reusable/base-components/HelpPageBtn'
import CloseIcon from '@/ui/icons/CloseIcon'

export default {
  components: {
    HelpPageBtn,
    CloseIcon,
    RequestPrice: () => import('./task-modals/request-price/RequestPrice'),
    PlanWorkflow: () => import('./task-modals/plan-workflow/PlanWorkflow'),

    PriceEntry: () => import('./task-modals/price-entry/PriceEntry'),
    ExpiringLicenses: () => import('./task-modals/expiring-licenses/ExpiringLicenses'),
    FinalizeLicenses: () => import('./task-modals/finalize-licenses/FinalizeLicenses'),
    TitleLists: () => import('./task-modals/title-lists/TitleLists'),

    Stakeholders: () => import('./task-modals/stakeholders/Stakeholders'),
    Evaluation: () => import('./task-modals/evaluation/Evaluation'),
    Decision: () => import('./task-modals/decision/Decision'),
    PlaceOrders: () => import('./task-modals/place-orders/PlaceOrders'),

    ApproveInvoices: () => import('./task-modals/approve-invoices/ApproveInvoices'),
    VendorPayments: () => import('./task-modals/vendor-payments/VendorPayments'),

    Implementation: () => import('./task-modals/implementation/Implementation'),
    InformUsers: () => import('./task-modals/inform-users/InformUsers')
  },

  props: {
    name: {
      type: String,
      required: true
    },

    id: {
      type: String,
      required: true
    },

    page: {
      type: Object,
      required: true
    },

    count: Number,

    periodId: Number
  },

  data: () => ({
    isDialogOpen: false,
    dialogName: '',
    dialogComponent: '',
    dialogLoader: false,
    subtitle: null
  }),

  computed: {
    isCount0() {
      return this.count === 0
    }
  },

  methods: {
    closeDialog(count) {
      this.$emit('update-count', count ?? this.$refs.dialogContent.getCount())
      this.subtitle = null
      this.isDialogOpen = false
    },

    openTaskDialog() {
      if (!this.count) return

      this.dialogLoader = true
      this.dialogName = this.name
      this.dialogComponent = startCase(this.name).replace(' ', '')
      this.isDialogOpen = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/shadows';

.task {
  height: 34px;
}

.task-modal {
  filter: drop-shadow(5px 5px 16px rgba(163, 177, 198, 0.5));
  overflow: hidden;
  isolation: isolate;

  .header {
    z-index: 1;
  }
}
</style>

<style lang="scss">
.workflow-task-modal > .q-dialog__backdrop {
  background: rgba(85, 89, 122, 0.5);
}
</style>
